import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import WarpTransition from './WarpTransition';

export const WARP_JUMP_MS = 1500;
export const WARP_NAVIGATION_MS = 1850;
export const WARP_ARRIVAL_MS = 2100;
export const WARP_TOTAL_MS = 4100;

const WarpTransitionContext = createContext(null);

export const useWarpTransition = () => {
  const context = useContext(WarpTransitionContext);

  if (!context) {
    throw new Error(
      'useWarpTransition must be used inside WarpTransitionProvider'
    );
  }

  return context;
};

export const useOptionalWarpTransition = () =>
  useContext(WarpTransitionContext);

const WarpTransitionProvider = ({ children }) => {
  const history = useHistory();
  const [transition, setTransition] = useState(null);
  const timersRef = useRef([]);
  const activeRef = useRef(false);
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const startTransition = useCallback(
    ({
      to,
      label = 'OPENING RELAY',
      variant = 'roll',
      onStart = () => {},
      onNavigate = () => {},
      onComplete = () => {},
      replace = false,
      departureMs = 0,
    }) => {
      if (activeRef.current) return false;

      activeRef.current = true;
      onStart();

      if (reduceMotion) {
        onNavigate();
        replace ? history.replace(to) : history.push(to);
        onComplete();
        activeRef.current = false;
        return true;
      }

      setTransition({ phase: 'charge', label, variant, departureMs });

      timersRef.current.push(
        setTimeout(() => {
          setTransition((current) =>
            current ? { ...current, phase: 'jump' } : current
          );
        }, departureMs + WARP_JUMP_MS),
        setTimeout(() => {
          onNavigate();
          replace ? history.replace(to) : history.push(to);
        }, departureMs + WARP_NAVIGATION_MS),
        setTimeout(() => {
          setTransition((current) =>
            current ? { ...current, phase: 'arrival' } : current
          );
        }, departureMs + WARP_ARRIVAL_MS),
        setTimeout(() => {
          setTransition(null);
          activeRef.current = false;
          timersRef.current = [];
          onComplete();
        }, departureMs + WARP_TOTAL_MS)
      );

      return true;
    },
    [history, reduceMotion]
  );

  const value = useMemo(
    () => ({
      isTransitioning: transition !== null,
      startTransition,
    }),
    [startTransition, transition]
  );

  return (
    <WarpTransitionContext.Provider value={value}>
      {children}
      {transition && <WarpTransition {...transition} />}
    </WarpTransitionContext.Provider>
  );
};

WarpTransitionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default WarpTransitionProvider;
